import React, { useState, useEffect } from 'react';
import { Autocomplete, Box, Checkbox, Chip, InputAdornment, Pagination, Skeleton, TextField, Typography } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import Close from '@mui/icons-material/Close';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import PersonSearchOutlined from '@mui/icons-material/PersonSearchOutlined';
import { useLocation, useHistory } from 'react-router-dom';
import { withSnackbar } from '../../utils/SnackbarHOC';
import { getAll, getYears } from '../../services/CollaborateurService';
import { getAllContrats } from '../../services/ContratService';
import CollaborateurCard from './components/CollaborateurCard';
import GenerateModal from './components/GenerateModal';
import { AppButton } from '../../components/UI';
import styles from './styles/Collaborateur.module.css';

const PAGE_SIZES = [12, 24, 48];
const DEFAULT_PAGE_SIZE = 48;
const SEARCH_DELAY_MS = 350;

const filterSx = {
  flex: '0 0 170px',
  width: 170,
  '& .MuiOutlinedInput-root': { borderRadius: '10px', background: '#fff', minHeight: 40, py: '2px !important' },
};

const quickChipSx = (active) => ({
  height: 26, fontWeight: 600, fontSize: '0.78rem',
  color: active ? '#fff' : '#475569',
  background: active ? '#03407b' : '#f8fafc',
  border: `1px solid ${active ? '#03407b' : '#e2e8f0'}`,
  '&:hover': { background: active ? '#02357a' : '#ebf4fb' },
});

// Multi-select filter shown as "Contrat (2)" once something is picked.
const MultiFilter = ({ label, options, value, onChange }) => (
  <Autocomplete
    multiple
    size="small"
    disableCloseOnSelect
    options={options}
    value={value}
    onChange={(e, v) => onChange(v)}
    getOptionLabel={(o) => o.label}
    isOptionEqualToValue={(o, v) => o.value === v.value}
    noOptionsText="Aucune option"
    renderOption={({ key, ...props }, o, { selected }) => (
      <li key={o.value} {...props}>
        <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0.25, '&.Mui-checked': { color: '#03407b' } }} />
        {o.label}
      </li>
    )}
    renderTags={(tags) => (
      <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#03407b', pl: 0.5 }}>
        {label} ({tags.length})
      </Typography>
    )}
    renderInput={(params) => <TextField {...params} placeholder={value.length ? '' : label} />}
    sx={filterSx}
  />
);

const Collaborateur = () => {
  const [employees, setEmployees] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedYears, setSelectedYears] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [yearOptions, setYearOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedContrats, setSelectedContrats] = useState([]);
  const [contratOptions, setContratOptions] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [initialized, setInitialized] = useState(false);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const history = useHistory();
  const location = useLocation();

  const updateURL = () => {
    const params = new URLSearchParams();
    if (searchText) params.set('search', searchText);
    if (selectedYears.length > 0) params.set('year', selectedYears.map(y => y.value).join(','));
    if (selectedContrats.length > 0) params.set('contrats', selectedContrats.map(c => c.value).join(','));
    if (pageSize !== DEFAULT_PAGE_SIZE) params.set('limit', pageSize);
    params.set('page', currentPage);
    history.replace({ pathname: location.pathname, search: params.toString() });
  };

  useEffect(() => {
    (async () => {
      try {
        const [yearsResponse, contratsResponse] = await Promise.all([getYears(), getAllContrats()]);
        setYearOptions(yearsResponse.data.map(year => ({ label: year.toString(), value: year.toString() })));
        setContratOptions(contratsResponse.data.map(c => ({ label: c.nom_contrat, value: c.nom_contrat })));
      } catch (error) {
      }
    })();
  }, []);

  // Restore the filters from the URL once, before the first fetch.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const search = params.get('search') || '';
    const year = params.get('year') || '';
    const contrat = params.get('contrats') || '';
    setSearchInput(search);
    setSearchText(search);
    if (year) setSelectedYears(year.split(',').map(y => ({ label: y, value: y })));
    if (contrat) setSelectedContrats(contrat.split(',').map(c => ({ label: c, value: c })));
    const limit = parseInt(params.get('limit') || '', 10);
    if (PAGE_SIZES.includes(limit)) setPageSize(limit);
    setCurrentPage(parseInt(params.get('page') || '1', 10));
    setInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Search only once the user stops typing, instead of one request per key.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== searchText) {
        setSearchText(searchInput);
        setCurrentPage(1);
      }
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  useEffect(() => {
    if (!initialized) return;
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const response = await getAll({
          page: currentPage,
          limit: pageSize,
          search: searchText,
          years: selectedYears.map(y => y.value),
          contrats: selectedContrats.map(c => c.value),
        });
        if (!active) return;
        const { data, total } = response.data;
        setEmployees(data || []);
        setTotalPages(Math.max(1, Math.ceil(total / pageSize)));
        setTotalCount(total);
      } catch (error) {
        console.error('Error fetching collaborateurs', error);
      } finally {
        if (active) setLoading(false);
      }
    })();
    updateURL();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, currentPage, pageSize, searchText, selectedYears, selectedContrats]);

  const hasFilters = searchText || selectedYears.length > 0 || selectedContrats.length > 0;
  const resetFilters = () => {
    setSearchInput('');
    setSearchText('');
    setSelectedYears([]);
    setSelectedContrats([]);
    setCurrentPage(1);
  };

  const startIdx = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endIdx = Math.min(currentPage * pageSize, totalCount);

  return (
    <div className={styles.container}>
      <GenerateModal open={openModal} onClose={() => setOpenModal(false)} />

      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2, px: 2, py: 1.5,
        borderRadius: '14px', background: '#fff', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(15,23,42,0.05)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mr: 1 }}>
          <Box sx={{ width: 38, height: 38, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ebf4fb', color: '#03407b' }}>
            <GroupsOutlined />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>Collaborateurs</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>
              {loading && !totalCount ? 'Chargement…' : `${totalCount} collaborateur${totalCount > 1 ? 's' : ''}${hasFilters ? ' trouvé' + (totalCount > 1 ? 's' : '') : ''}`}
            </Typography>
          </Box>
        </Box>

        <TextField
          size="small"
          placeholder="Rechercher un collaborateur…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ flex: '1 1 260px', maxWidth: 420, '& .MuiOutlinedInput-root': { borderRadius: '10px', background: '#fff' } }} slotProps={{ input: {
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>,
            endAdornment: searchInput && (
              <InputAdornment position="end">
                <Close onClick={() => setSearchInput('')} sx={{ fontSize: 18, color: '#94a3b8', cursor: 'pointer', '&:hover': { color: '#0f172a' } }} />
              </InputAdornment>
            ),
          } }}
        />

        <MultiFilter label="Année" options={yearOptions} value={selectedYears}
          onChange={(v) => { setSelectedYears(v); setCurrentPage(1); }} />

        {hasFilters && (
          <Chip label="Réinitialiser" size="small" onDelete={resetFilters} onClick={resetFilters}
            sx={{ height: 28, fontWeight: 600, color: '#475569', background: '#f1f5f9' }} />
        )}

        <Box sx={{ ml: 'auto' }}>
          <AppButton variant="primary" size="small" startIcon={<SettingsIcon fontSize="small" />} onClick={() => setOpenModal(true)}>
            Générer un document
          </AppButton>
        </Box>

        {contratOptions.length > 0 && (
          <Box sx={{ flexBasis: '100%', display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', pt: 1.25, borderTop: '1px solid #f1f5f9' }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', letterSpacing: '0.06em', textTransform: 'uppercase', mr: 0.5 }}>
              Contrat
            </Typography>
            <Chip label="Tous" size="small" onClick={() => { setSelectedContrats([]); setCurrentPage(1); }}
              sx={quickChipSx(selectedContrats.length === 0)} />
            {contratOptions.map((o) => {
              const active = selectedContrats.some((c) => c.value === o.value);
              return (
                <Chip key={o.value} label={o.label} size="small"
                  onClick={() => {
                    setSelectedContrats(active ? selectedContrats.filter((c) => c.value !== o.value) : [...selectedContrats, o]);
                    setCurrentPage(1);
                  }}
                  sx={quickChipSx(active)} />
              );
            })}
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
        {loading
          ? Array.from({ length: pageSize }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={170} sx={{ borderRadius: '14px' }} />
          ))
          : employees.map((employee) => <CollaborateurCard key={employee.id} employee={employee} />)}
      </Box>

      {!loading && employees.length === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 8, color: '#94a3b8' }}>
          <PersonSearchOutlined sx={{ fontSize: 40 }} />
          <Typography sx={{ fontSize: '0.95rem', color: '#475569', fontWeight: 600 }}>Aucun collaborateur trouvé</Typography>
          {hasFilters && (
            <AppButton variant="secondary" size="small" onClick={resetFilters}>Réinitialiser les filtres</AppButton>
          )}
        </Box>
      )}

      {!loading && totalCount > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '0.85rem', color: '#64748b' }}>
              {startIdx}–{endIdx} sur {totalCount}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8', mr: 0.25 }}>Par page</Typography>
              {PAGE_SIZES.map((size) => (
                <Chip key={size} label={size} size="small"
                  onClick={() => { setPageSize(size); setCurrentPage(1); }}
                  sx={quickChipSx(pageSize === size)} />
              ))}
            </Box>
          </Box>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(e, page) => setCurrentPage(page)}
            shape="rounded"
            sx={{
              '& .MuiPaginationItem-root': { fontWeight: 600 },
              '& .Mui-selected': { background: '#03407b !important', color: '#fff' },
            }}
          />
        </Box>
      )}
    </div>
  );
};

export default withSnackbar(Collaborateur);
